import { inject, Injectable, LOCALE_ID, type Provider } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageEnum, PreferredLanguageEnum } from '@reconcept-platform/shared/types';

const PREFERRED_LANGUAGE_STORAGE_KEY = 'preferred-language';

// A portfolio format may have no language chosen yet, a user always has one.
export function toPreferredLanguage(language: LanguageEnum | null | undefined): PreferredLanguageEnum {
  return language === LanguageEnum.EN ? PreferredLanguageEnum.EN : PreferredLanguageEnum.NL;
}

export function clearPreferredLanguageFromSession(): void {
  // sessionStorage is not available in SSR
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(PREFERRED_LANGUAGE_STORAGE_KEY);
  }
}

export function getPreferredLanguagesFromBrowsers(): readonly string[] {
  // navigator is not available in SSR
  if (typeof navigator === 'undefined') {
    return [];
  }

  return navigator.languages;
}

export function getLanguageCode(): string {
  const browserLanguages = getPreferredLanguagesFromBrowsers(),
    browserLanguage = browserLanguages
      .map((language) => {
        // extract language sub tag from browser languages
        // chrome returns language tags like 'en-US', 'nl-NL' and 'en', 'nl'.
        // safari on iOS returns 'en-US' and 'nl-NL' only.
        return language.split('-')[0];
      })
      .find((language) => Object.keys(PreferredLanguageEnum).includes(language.toUpperCase()));

  // sessionStorage is not available in SSR
  const sessionLanguage =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(PREFERRED_LANGUAGE_STORAGE_KEY) : undefined;

  // we use english as the fallback language, because unknown language more often speak english than dutch
  const fallbackLanguage = 'en';

  return sessionLanguage ?? browserLanguage ?? fallbackLanguage;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageSetter {
  private translateService = inject(TranslateService);

  setLanguage(preferredLanguage?: PreferredLanguageEnum): void {
    const language = preferredLanguage ? preferredLanguage.toLowerCase() : getLanguageCode();

    // sessionStorage is not available in SSR
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, language);
    }

    this.translateService.use(language);
  }
}

export class LocaleId extends String {
  constructor(
    private translateService: TranslateService,
    private languageSetter: LanguageSetter,
  ) {
    super();
  }

  override toString(): string {
    if (!this.translateService.currentLang()) {
      this.languageSetter.setLanguage(); // set language according to user or browser preferences
    }
    // @ts-expect-error TS2322 type not assignable
    return this.translateService.currentLang();
  }

  override valueOf(): string {
    return this.toString();
  }
}

export const LocaleProvider: Provider = {
  provide: LOCALE_ID,
  useClass: LocaleId,
  deps: [TranslateService],
};
